import { useState, useEffect } from 'react';
import { formatDateMedium } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import { StatusBadge } from '../../../../components/DataTable';

interface PosRoom {
  id: string;
  room_name?: string;
  room_number?: string;
  bed_count?: number;
  room_type?: string;
  floor?: string;
  status?: string;
  last_service_date?: string;
  daysSinceService?: number | null;
  notes?: string;
}

interface RoomViewModalProps {
  room: PosRoom;
  property: { id: string; tier?: string };
  onClose: () => void;
  onEdit: () => void;
  canEdit: boolean;
  supabase: SupabaseClient;
}

interface ServiceRecord {
  id: string;
  service_date: string | null;
  invoice_number: string | null;
  service_type: string | null;
}

export default function RoomViewModal({ room, onClose, onEdit, canEdit, supabase }: RoomViewModalProps) {
  const [services, setServices] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServiceHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [room.id]);

  const loadServiceHistory = async (): Promise<void> => {
    try {
      // Completed services live in hosp_service_history (one row per
      // invoice × room fan-out). hosp_service_requests is the bookings
      // side — that's what the old query hit, and why history always
      // came back empty for POS properties.
      const { data } = await supabase
        .from('hosp_service_history')
        .select('id, service_date, service_type, invoices(invoice_number)')
        .eq('room_id', room.id)
        .order('service_date', { ascending: false })
        .limit(10);
      const rows: ServiceRecord[] = (data || []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        service_date: r.service_date as string | null,
        service_type: (r.service_type as string | null) || 'standard',
        invoice_number: (r.invoices as { invoice_number?: string } | null)?.invoice_number || null,
      }));
      setServices(rows);
    } catch (err: unknown) {
      console.error('Error loading service history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return formatDateMedium(dateStr);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
        <div className="modal-header">
          <h2 className="modal-title">{room.room_name || room.room_number || 'Room Details'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="flex flex-col gap-lg">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div className="text-xs text-muted mb-xs">Room Number</div>
                <div className="fw-500">{room.room_number || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-xs">Room Name</div>
                <div className="fw-500">{room.room_name || '-'}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div className="text-xs text-muted mb-xs">Bed Count</div>
                <div className="fw-500">{room.bed_count || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-xs">Room Type</div>
                <div className="fw-500">{room.room_type || '-'}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div className="text-xs text-muted mb-xs">Floor</div>
                <div>{room.floor || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-muted mb-xs">Status</div>
                <StatusBadge
                  bg={(room.status || 'active') === 'active' ? 'var(--color-success-bg)' : 'var(--color-border)'}
                  color={(room.status || 'active') === 'active' ? 'var(--color-success)' : 'var(--color-text-light)'}
                >
                  {(room.status || 'active') === 'active' ? 'Active' : 'Inactive'}
                </StatusBadge>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted mb-xs">Last Service</div>
              <div className="fw-500">
                {room.last_service_date ? formatDate(room.last_service_date) : 'Never serviced'}
              </div>
              {room.daysSinceService !== null && (
                <div className="text-xs text-muted">
                  {room.daysSinceService} days ago
                </div>
              )}
            </div>
            {room.notes && (
              <div>
                <div className="text-xs text-muted mb-xs">Notes</div>
                <div className="text-sm">{room.notes}</div>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
              <div className="text-xs text-muted mb-sm">
                Service History (last 10)
              </div>
              {loading ? (
                <div style={{ color: 'var(--color-text-light)', fontStyle: 'italic' }}>Loading...</div>
              ) : services.length === 0 ? (
                <div style={{ color: 'var(--color-text-light)', fontStyle: 'italic' }}>No service records</div>
              ) : (
                <div className="flex flex-col gap-sm">
                  {services.map(svc => (
                    <div
                      key={svc.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)'
                      }}
                    >
                      <span>{formatDate(svc.service_date)}</span>
                      {svc.invoice_number && (
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', fontFamily: 'monospace' }}>
                          {svc.invoice_number}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canEdit && (
            <button className="btn btn-primary" onClick={onEdit}>Edit Room</button>
          )}
        </div>
      </div>
    </div>
  );
}
